/**
 * useLocalization — Enterprise Localization Composable
 *
 * Single entry point for ALL frontend localization.
 *
 * Architecture:
 *   en.json (master) → Backend API → TranslationOverride → TranslationCache → UI
 *
 * Translation priority (resolved by backend):
 *   1️⃣  Admin override
 *   2️⃣  Cached translation (DB)
 *   3️⃣  English fallback
 *
 * Usage:
 *   const { t, translate, setLocale } = useLocalization()
 *
 *   t('nav.dashboard')                            // static UI label (sync)
 *   const text = await translate('Revenue fell')   // dynamic content (async)
 *   await setLocale('ta-IN')                       // switch to Tamil
 *
 * Infrastructure cost: $0 (LibreTranslate + Neon PostgreSQL free tiers)
 */

import { ref, computed, readonly, type Ref, type ComputedRef } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/api/client";
import {
  SUPPORTED_LOCALES,
  enMessages,
  setLocaleMessages,
  initLocaleWithEnglishBase,
} from "@/i18n";
import { resolveLocalizationKey } from "@/utils/localizationResolver";
import { getManifestForRoute } from "@/localization/routeManifestMap";
import { clearRoutePrefetchCache } from "@/localization/routePrefetch";
import {
  normalizeLocalizationKey,
  humanizeKey,
} from "@/localization/translationResolver";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TranslationResult {
  sourceText: string;
  translatedText: string;
  source: "override" | "cache" | "provider" | "fallback";
}

export interface UseLocalizationReturn {
  /** Translate a static i18n key (sync — Vue I18n). */
  t: (key: string, ...args: unknown[]) => string;
  /** Safe translation with key normalization and humanized fallback. */
  tSafe: (key: string) => string;
  /** Translate dynamic text through the backend chain (async). */
  translate: (text: string) => Promise<string>;
  /** Batch-translate dynamic texts in one request (async). */
  translateBatch: (texts: string[]) => Promise<string[]>;
  /** Switch locale — pre-translates all static keys via backend. */
  setLocale: (code: string) => Promise<void>;
  /** Auto-detect locale: user pref → company default → browser → 'en'. */
  detectLocale: (
    userPref?: string | null,
    companyDefault?: string | null,
  ) => Promise<void>;
  /** Prefetch translations for a route's manifest (called by router guard). */
  prefetchRouteTranslations: (routePath: string) => Promise<void>;
  /** Current locale code (reactive). */
  currentLocale: Ref<string>;
  /** True while a locale switch is in progress. */
  isLoading: Readonly<Ref<boolean>>;
  /** True if current locale is RTL (Arabic, Urdu, Sindhi, Hebrew). */
  isRTL: ComputedRef<boolean>;
  /** Full list of supported locales. */
  supportedLocales: typeof SUPPORTED_LOCALES;
  /** Clear the in-memory translation cache. */
  clearCache: () => void;
}

// ── Module-level singletons ───────────────────────────────────────────────────

/** In-memory translation cache. Key: `${locale}::${text}` → translated text. */
const memoryCache = new Map<string, string>();

/** RTL locale codes. */
const RTL_LOCALES = new Set(["ar", "ur", "sd", "he"]);

/** Tracks locales whose static keys have already been pre-translated this session. */
const preTranslatedLocales = new Set<string>();

/** Tracks route+locale combos whose manifests have been prefetched. */
const prefetchedRoutes = new Set<string>();

/** Returns true only if there is a valid access token in localStorage. */
function isAuthenticated(): boolean {
  return !!localStorage.getItem("access_token");
}

// ── Auto-batching microtask queue ─────────────────────────────────────────────
// Multiple translate() calls in the same tick are coalesced into one batch
// request, reducing network round-trips.

interface PendingItem {
  text: string;
  resolve: (value: string) => void;
}
let pendingQueue: PendingItem[] = [];
let flushScheduled = false;

async function flushQueue(locale: string): Promise<void> {
  const batch = pendingQueue.splice(0);
  flushScheduled = false;
  if (!batch.length) return;

  // Silently fall back to English text if not authenticated
  if (!isAuthenticated()) {
    batch.forEach((item) => item.resolve(item.text));
    return;
  }

  const texts = batch.map((p) => p.text);
  try {
    const res = await api.post("/localization/translate-batch", {
      texts,
      locale,
    });
    const results: TranslationResult[] = res.data?.data ?? [];
    const resultMap = new Map(
      results.map((r) => [r.sourceText, r.translatedText]),
    );
    batch.forEach((item) => {
      const translated = resultMap.get(item.text) ?? item.text;
      memoryCache.set(`${locale}::${item.text}`, translated);
      item.resolve(translated);
    });
  } catch {
    // On error fall back to English text so UI never shows raw keys
    batch.forEach((item) => item.resolve(item.text));
  }
}

// ── Flatten / unflatten (for batch pre-translating en.json) ───────────────────

function flattenJSON(
  obj: Record<string, unknown>,
  prefix = "",
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      Object.assign(
        result,
        flattenJSON(value as Record<string, unknown>, fullKey),
      );
    } else if (typeof value === "string") {
      result[fullKey] = value;
    }
  }
  return result;
}

function unflattenJSON(flat: Record<string, string>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split(".");
    let cur: Record<string, unknown> = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!(parts[i] in cur) || typeof cur[parts[i]] !== "object") {
        cur[parts[i]] = {};
      }
      cur = cur[parts[i]] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = value;
  }
  return result;
}

/**
 * Batch-translate ALL en.json values for a locale via the backend.
 * Results are registered as Vue I18n messages so t(key) is synchronous.
 */
async function preTranslateStaticKeys(locale: string): Promise<void> {
  if (locale === "en") return;

  // Skip if already translated this session (avoids repeated API bursts)
  if (preTranslatedLocales.has(locale)) return;

  // Skip if not authenticated — UI will show English fallback text
  if (!isAuthenticated()) return;

  const flat = flattenJSON(enMessages as Record<string, unknown>);
  const entries = Object.entries(flat);
  if (!entries.length) return;

  const BATCH_SIZE = 50;
  const translatedFlat: Record<string, string> = {};

  for (let i = 0; i < entries.length; i += BATCH_SIZE) {
    const chunk = entries.slice(i, i + BATCH_SIZE);
    const texts = chunk.map(([, v]) => v);

    try {
      const res = await api.post("/localization/translate-batch", {
        texts,
        locale,
      });
      const results: TranslationResult[] = res.data?.data ?? [];
      const resultMap = new Map(
        results.map((r) => [r.sourceText, r.translatedText]),
      );
      chunk.forEach(([key, value]) => {
        translatedFlat[key] = resultMap.get(value) ?? value;
      });
    } catch {
      // On error use English values for this chunk — user sees English, not raw keys
      chunk.forEach(([key, value]) => {
        translatedFlat[key] = value;
      });
    }
  }

  const translatedMessages = unflattenJSON(translatedFlat);
  setLocaleMessages(locale, translatedMessages);
  preTranslatedLocales.add(locale);
}

// ── Composable ────────────────────────────────────────────────────────────────

export function useLocalization(): UseLocalizationReturn {
  const { t: i18nT, locale } = useI18n();
  const isLoading = ref(false);

  // ── t(key) ────────────────────────────────────────────────────────────────
  // Production-safe resolver: Vue I18n is tried first (returns translated text
  // once setLocaleMessages has run); if it returns the raw key (missing
  // message, fallback chain failed, or locale not yet initialised) we recover
  // immediately from the statically-bundled en.json via resolveLocalizationKey.
  // This guarantees the UI NEVER shows raw i18n keys in any environment.
  const t = (key: string, ...args: unknown[]): string => {
    const i18nResult = (i18nT as (k: string, ...a: unknown[]) => string)(
      key,
      ...args,
    );
    // Vue I18n returns the key itself when no message is found
    if (i18nResult === key) return resolveLocalizationKey(key);
    return i18nResult;
  };

  // ── tSafe(key) ──────────────────────────────────────────────────────────
  // Normalizes legacy keys (DASHBOARD.TOTALMRR → dashboard.totalMRR),
  // attempts Vue I18n lookup, then falls back to resolveLocalizationKey,
  // and finally humanizes the key so the user NEVER sees dotted key strings.
  const tSafe = (key: string): string => {
    const normalized = normalizeLocalizationKey(key);
    const i18nResult = (i18nT as (k: string) => string)(normalized);
    // i18n returns the key itself when no message is found
    if (i18nResult !== normalized) return i18nResult;
    // Try en.json resolver
    const resolved = resolveLocalizationKey(normalized);
    if (resolved !== normalized) return resolved;
    // Last resort: human-readable label from the key
    return humanizeKey(normalized);
  };

  // ── translate(text) ───────────────────────────────────────────────────────
  async function translate(text: string): Promise<string> {
    if (!text?.trim()) return text;
    const loc = locale.value;
    if (loc === "en") return text;

    const cacheKey = `${loc}::${text}`;
    if (memoryCache.has(cacheKey)) return memoryCache.get(cacheKey)!;

    return new Promise<string>((resolve) => {
      pendingQueue.push({ text, resolve });
      if (!flushScheduled) {
        flushScheduled = true;
        Promise.resolve().then(() => flushQueue(loc));
      }
    });
  }

  // ── translateBatch(texts) ─────────────────────────────────────────────────
  async function translateBatch(texts: string[]): Promise<string[]> {
    if (!texts.length) return [];
    const loc = locale.value;
    if (loc === "en") return texts;

    const uncached: string[] = [];
    const results = new Map<string, string>();

    texts.forEach((text) => {
      const cacheKey = `${loc}::${text}`;
      if (memoryCache.has(cacheKey)) {
        results.set(text, memoryCache.get(cacheKey)!);
      } else {
        uncached.push(text);
      }
    });

    if (uncached.length && isAuthenticated()) {
      try {
        const res = await api.post("/localization/translate-batch", {
          texts: uncached,
          locale: loc,
        });
        const data: TranslationResult[] = res.data?.data ?? [];
        data.forEach((r) => {
          memoryCache.set(`${loc}::${r.sourceText}`, r.translatedText);
          results.set(r.sourceText, r.translatedText);
        });
      } catch {
        uncached.forEach((text) => results.set(text, text));
      }
    } else {
      uncached.forEach((text) => results.set(text, text));
    }

    return texts.map((text) => results.get(text) ?? text);
  }

  // ── setLocale(code) ───────────────────────────────────────────────────────
  async function setLocale(code: string): Promise<void> {
    const base = code.split("-")[0].toLowerCase();
    isLoading.value = true;
    try {
      // Only clear the dynamic translation cache (memoryCache).
      // Do NOT clear preTranslatedLocales — this lets switching back to a
      // previously-loaded locale be instant (no re-fetch).
      memoryCache.clear();

      // Clear route prefetch dedup so routes re-prefetch in the new locale
      clearRoutePrefetchCache();
      prefetchedRoutes.clear();

      // Set English base so t() works immediately (never shows raw keys)
      if (base !== "en") {
        initLocaleWithEnglishBase(base);
      }

      // Batch-translate all static keys via backend (memoised — skips if done)
      await preTranslateStaticKeys(base);

      locale.value = base;

      // Apply sensible regional defaults (currency, timezone, number format)
      // for the chosen language so formatters reflect the language switch.
      try {
        const { useRegionStore } = await import("@/stores/region");
        useRegionStore().applyLanguageDefaults(base);
      } catch {
        /* region store may not be ready — silent */
      }

      // RTL
      document.documentElement.dir = RTL_LOCALES.has(base) ? "rtl" : "ltr";
      document.documentElement.lang = base;
    } finally {
      isLoading.value = false;
    }
  }

  // ── detectLocale() ────────────────────────────────────────────────────────
  async function detectLocale(
    userPref?: string | null,
    companyDefault?: string | null,
  ): Promise<void> {
    const codes = SUPPORTED_LOCALES.map((l) => l.code) as string[];
    let best = "en";

    if (userPref && codes.includes(userPref)) {
      best = userPref;
    } else if (companyDefault && codes.includes(companyDefault)) {
      best = companyDefault;
    } else {
      const browserLocale = navigator.language ?? "en";
      if (codes.includes(browserLocale)) {
        best = browserLocale;
      } else {
        const langOnly = browserLocale.split("-")[0];
        if (codes.includes(langOnly)) best = langOnly;
      }
    }

    if (best !== locale.value) {
      await setLocale(best);
    }
  }

  // ── prefetchRouteTranslations(routePath) ─────────────────────────────────
  // Called by the router beforeEach guard. Resolves the route's manifest
  // keys to English text, filters out already-cached translations, and
  // batch-translates only the uncached ones in a single API call.
  async function prefetchRouteTranslations(routePath: string): Promise<void> {
    const loc = locale.value;
    if (loc === "en") return;
    if (!isAuthenticated()) return;

    const manifestKey = `${loc}::${routePath}`;
    if (prefetchedRoutes.has(manifestKey)) return;

    const manifest = getManifestForRoute(routePath);
    if (!manifest || !manifest.length) return;

    // Resolve each key to its English source text and filter out cache hits
    const uncachedTexts: string[] = [];
    const keyToText = new Map<string, string>();

    for (const key of manifest) {
      const englishText = resolveLocalizationKey(key);
      // Skip keys that didn't resolve (returns the key itself)
      if (englishText === key) continue;
      keyToText.set(key, englishText);

      const cacheKey = `${loc}::${englishText}`;
      if (!memoryCache.has(cacheKey)) {
        uncachedTexts.push(englishText);
      }
    }

    // Only call the API if there are uncached translations
    if (uncachedTexts.length) {
      try {
        const res = await api.post("/localization/translate-batch", {
          texts: uncachedTexts,
          locale: loc,
        });
        const results: TranslationResult[] = res.data?.data ?? [];
        results.forEach((r) => {
          memoryCache.set(`${loc}::${r.sourceText}`, r.translatedText);
        });
      } catch {
        // Silently fall back — English text will be shown by t()
      }
    }

    prefetchedRoutes.add(manifestKey);

    // Dev-mode: warn about manifest keys that didn't resolve
    if (import.meta.env.DEV) {
      for (const key of manifest) {
        if (!keyToText.has(key)) {
          console.warn(
            `[Localization] Manifest key not found in en.json: ${key}`,
          );
        }
      }
    }
  }

  // ── clearCache() ────────────────────────────────────────────────────────
  function clearCache(): void {
    memoryCache.clear();
    preTranslatedLocales.clear();
    prefetchedRoutes.clear();
  }

  // ── isRTL ─────────────────────────────────────────────────────────────────
  const isRTL = computed(() => RTL_LOCALES.has(locale.value));

  return {
    t,
    tSafe,
    translate,
    translateBatch,
    setLocale,
    detectLocale,
    prefetchRouteTranslations,
    currentLocale: locale,
    isLoading: readonly(isLoading),
    isRTL,
    supportedLocales: SUPPORTED_LOCALES,
    clearCache,
  };
}

export type { UseLocalizationReturn as UseLocalization };
